import * as React from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { fetchDayOfWeekByWeekday } from '@src/api';
import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { formatDateToYMD } from '@src/utils/dateUtils';

// 0 = Monday … 6 = Sunday (matches the backend's stored `weekday`).
const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const safeDiv = (numerator, denominator) => {
  const num = safeNumber(numerator);
  const denom = safeNumber(denominator);
  return denom ? num / denom : 0;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const SUM_FIELDS = [
  'total_spend_sum',
  'total_leads_sum',
  'booked_sum',
  'scheduled_sum',
  'issues_sum',
  'demos_sum',
  'gross_sales_sum',
  'net_sales_sum',
  'net_amount_sum',
  'cohort_issues_sum',
  'cohort_demos_sum',
  'cohort_gross_sales_sum',
  'cohort_net_sales_sum',
  'cohort_net_amount_sum',
];

function normalizeRows(summaryRows = []) {
  const byWeekday = new Map();

  (summaryRows || []).forEach((row = {}) => {
    const weekday = safeNumber(row.weekday);
    const normalized = {
      weekday,
      label: row.weekday_name || WEEKDAY_NAMES[weekday] || String(weekday),
    };
    SUM_FIELDS.forEach((field) => {
      normalized[field] = safeNumber(row[field]);
    });
    byWeekday.set(weekday, normalized);
  });

  // Always render all seven days in order, even with no data for a day.
  return WEEKDAY_NAMES.map((label, weekday) => {
    const existing = byWeekday.get(weekday);
    if (existing) {
      return existing;
    }
    const empty = { weekday, label };
    SUM_FIELDS.forEach((field) => {
      empty[field] = 0;
    });
    return empty;
  });
}

// Map a summary row to the funnel numbers for the selected attribution mode.
// Cohort mode rolls issue/demo/sale outcomes back onto the lead's created
// date ("how do leads created on this day eventually perform"); activity mode
// keeps event-date attribution ("what happened on this day"). Leads, spend
// and booked are created-date based in both modes.
function resolveRow(row = {}, mode = 'cohort') {
  const cohort = mode === 'cohort';
  return {
    spend: safeNumber(row.total_spend_sum),
    leads: safeNumber(row.total_leads_sum),
    booked: safeNumber(row.booked_sum),
    issues: safeNumber(cohort ? row.cohort_issues_sum : row.issues_sum),
    demos: safeNumber(cohort ? row.cohort_demos_sum : row.demos_sum),
    netSold: safeNumber(cohort ? row.cohort_net_sales_sum : row.net_sales_sum),
    netAmount: safeNumber(
      cohort ? row.cohort_net_amount_sum : row.net_amount_sum
    ),
  };
}

function buildTotals(rows = [], mode = 'cohort') {
  return rows.reduce(
    (acc, row = {}) => {
      const values = resolveRow(row, mode);
      acc.spend += values.spend;
      acc.leads += values.leads;
      acc.booked += values.booked;
      acc.issues += values.issues;
      acc.demos += values.demos;
      acc.netSold += values.netSold;
      acc.netAmount += values.netAmount;
      return acc;
    },
    { spend: 0, leads: 0, booked: 0, issues: 0, demos: 0, netSold: 0, netAmount: 0 }
  );
}

const compactTableSx = {
  '& .MuiTableCell-root': { py: 0.75, px: 1.25, whiteSpace: 'nowrap' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 700,
    bgcolor: 'background.neutral',
  },
};

function MetricsRow({ label, row, totalLeads, mode }) {
  const { spend, leads, booked, issues, demos, netSold, netAmount } =
    resolveRow(row, mode);

  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(leads, totalLeads)} /></TableCell>
      <TableCell><DollarFormatter value={spend} /></TableCell>
      <TableCell>{leads}</TableCell>
      <TableCell><DollarFormatter value={safeDiv(spend, leads)} /></TableCell>
      <TableCell>{booked}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(booked, leads)} /></TableCell>
      <TableCell>{issues}</TableCell>
      <TableCell>{demos}</TableCell>
      <TableCell>{netSold}</TableCell>
      <TableCell><PercentageFormatter value={safeDiv(netSold, demos)} /></TableCell>
      <TableCell><DollarFormatter value={netAmount} /></TableCell>
      <TableCell><DollarFormatter value={safeDiv(netAmount, netSold)} /></TableCell>
      <TableCell><DollarFormatter value={safeDiv(netAmount, leads)} /></TableCell>
      <TableCell><PercentageFormatter value={safeDiv(spend, netAmount)} /></TableCell>
    </TableRow>
  );
}

export default function DayOfWeekPerformancePage() {
  const defaultRange = React.useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = React.useState(defaultRange.start);
  const [endDate, setEndDate] = React.useState(defaultRange.end);
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [attributionMode, setAttributionMode] = React.useState('cohort');
  const [rawRows, setRawRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientInputChange,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();

  React.useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ...(selectedClientId ? { client_id: selectedClientId } : {}),
        };
        const response = await fetchDayOfWeekByWeekday(payload);
        if (!active) {
          return;
        }
        setRawRows(Array.isArray(response?.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to load day-of-week performance data', err);
        if (active) {
          setError('Failed to load day-of-week performance data.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [startDate, endDate, selectedClientId]);

  const rows = React.useMemo(() => normalizeRows(rawRows), [rawRows]);
  const totals = React.useMemo(
    () => buildTotals(rows, attributionMode),
    [rows, attributionMode]
  );

  const handleDateRangeChange = React.useCallback((ranges) => {
    const nextStart = ranges?.selection?.startDate;
    const nextEnd = ranges?.selection?.endDate;
    if (nextStart instanceof Date && nextEnd instanceof Date) {
      setStartDate(nextStart);
      setEndDate(nextEnd);
    }
  }, []);

  return (
    <Page title="Day of Week Performance" fullWidth isLoading={loading}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5">Day of Week Performance</Typography>
          <Typography variant="body2" color="text.secondary">
            Spend, lead funnel and revenue collapsed into the seven days of the
            week across the selected window. Lead Cohort credits downstream
            results (issues, demos, sales) to the day the lead was created;
            Activity shows what happened on each day. Use it to spot which
            days convert best; move the date range to see how the pattern
            shifts over time.
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 1.25, md: 1.5 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                style={{ width: '100%' }}
              />
            </Box>
            <Autocomplete
              sx={{ minWidth: 260 }}
              options={clientOptions}
              loading={clientsLoading}
              value={findClientOptionByValue(selectedClientId)}
              onInputChange={handleClientInputChange}
              onChange={(_event, option) => setSelectedClientId(option ? String(option.value) : '')}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              getOptionLabel={(option) => option?.label || ''}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="All clients" />
              )}
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={attributionMode}
              onChange={(_event, next) => {
                if (next) {
                  setAttributionMode(next);
                }
              }}
            >
              <ToggleButton value="cohort" sx={{ whiteSpace: 'nowrap' }}>
                Lead Cohort
              </ToggleButton>
              <ToggleButton value="activity" sx={{ whiteSpace: 'nowrap' }}>
                Activity
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Paper>

        {attributionMode === 'cohort' ? (
          <Alert severity="info">
            Cohort view: every downstream metric (issues, demos, net sold,
            revenue) is credited to the day the lead was created, no matter
            when the appointment ran or the deal closed. Leads created in the
            last few weeks may not have matured yet, so recent windows will
            understate demos and sales.
          </Alert>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Card>
          <CardHeader
            title="Performance by Day of Week"
            subheader={
              attributionMode === 'cohort'
                ? 'By lead created date: each row is the eventual funnel of leads created on that weekday (Monday → Sunday).'
                : 'By event date: appointments and closings that happened on that weekday (Monday → Sunday).'
            }
          />
          <CardContent>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={compactTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell>Lead Mix</TableCell>
                    <TableCell>Spend</TableCell>
                    <TableCell>Leads</TableCell>
                    <TableCell>CPL</TableCell>
                    <TableCell>Booked</TableCell>
                    <TableCell>Set Rate</TableCell>
                    <TableCell>Issues</TableCell>
                    <TableCell>Demos</TableCell>
                    <TableCell>Net Sold</TableCell>
                    <TableCell>Close Rate</TableCell>
                    <TableCell>Net Amount</TableCell>
                    <TableCell>Avg Deal</TableCell>
                    <TableCell>RPL</TableCell>
                    <TableCell>Cost of Marketing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {totals.leads || totals.spend ? (
                    <>
                      {rows.map((row) => (
                        <MetricsRow
                          key={row.weekday}
                          label={row.label}
                          row={row}
                          totalLeads={totals.leads}
                          mode={attributionMode}
                        />
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell />
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={totals.spend} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.leads}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.spend, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.booked}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.booked, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.issues}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.demos}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{totals.netSold}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.netSold, totals.demos)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={totals.netAmount} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.netAmount, totals.netSold)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <DollarFormatter value={safeDiv(totals.netAmount, totals.leads)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <PercentageFormatter value={safeDiv(totals.spend, totals.netAmount)} />
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={15} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {loading ? 'Loading…' : 'No data for the selected range.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>
    </Page>
  );
}
